import { PLATFORM_LABELS } from "./constants";

const PLATFORM_INTEGRATION_IDS = {
  linkedin: "int_linkedin",
  x: "int_x",
  twitter: "int_x",
  facebook: "int_facebook",
  instagram: "int_instagram",
  youtube_shorts: "int_youtube",
  threads: "int_threads",
  bluesky: "int_bluesky",
};

const CONNECTED_MODES = new Set(["connected_account", "connected"]);

const byScheduleAsc = (a, b) => new Date(a?.scheduled_at || 0) - new Date(b?.scheduled_at || 0);

export function getCalendarItemPublishingContext(item = {}, assets = [], integrations = []) {
  const asset = item.asset_id ? assets.find((candidate) => candidate.id === item.asset_id) : null;
  const integrationId = PLATFORM_INTEGRATION_IDS[item.platform];
  const integration = integrations.find((candidate) => candidate.id === integrationId);
  const connected = Boolean(integration?.connected);
  const platformLabel = PLATFORM_LABELS[item.platform] || item.platform || "Platform";
  const mode = item.publish_mode || asset?.publish_mode || (connected ? "connected_account" : "manual_queue");
  const accountLabel = item.scheduled_account_label || asset?.scheduled_account_label || (
    CONNECTED_MODES.has(mode)
      ? `${platformLabel} connected account`
      : `${platformLabel} manual queue`
  );

  return {
    accountLabel,
    mode,
    connected: CONNECTED_MODES.has(mode) || connected,
    modeLabel: CONNECTED_MODES.has(mode) || connected ? "Connected publishing" : "Manual queue",
    integrationName: integration?.name || platformLabel,
  };
}

export function enrichCalendarItems(calendar = [], assets = [], integrations = []) {
  return calendar.map((item) => ({
    ...item,
    publishingContext: getCalendarItemPublishingContext(item, assets, integrations),
  }));
}

export function buildCalendarWorkspace({ calendar = [], assets = [], integrations = [], now = new Date() } = {}) {
  const nowTime = new Date(now).getTime();
  const enriched = enrichCalendarItems(calendar, assets, integrations);
  const upcoming = enriched
    .filter((item) => new Date(item.scheduled_at).getTime() >= nowTime && item.status !== "published")
    .sort(byScheduleAsc);

  const metrics = enriched.reduce((acc, item) => {
    if (item.status === "scheduled") acc.scheduled += 1;
    if (item.status === "needs_review" || item.status === "draft") acc.needsReview += 1;
    if (item.status === "published") acc.published += 1;
    if (item.status === "failed") acc.failed += 1;
    if (item.publishingContext.connected) acc.connected += 1;
    else acc.manual += 1;
    return acc;
  }, {
    scheduled: 0,
    needsReview: 0,
    published: 0,
    failed: 0,
    connected: 0,
    manual: 0,
  });

  const platformCounts = enriched.reduce((acc, item) => {
    const key = item.platform || "unknown";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  return {
    items: enriched,
    upcoming,
    nextItem: upcoming[0] || null,
    metrics,
    platformCounts,
  };
}
